import type { DetectedPos } from '../../../shared/enums';
import type { CorridorPlace, RouteStop } from '../../../shared/types';
import { apiFetch } from './api';

/** Corridor API client (M2). Both endpoints need the network — the Route tab is an online
 *  planning tool; the produced stop list itself works offline from localStorage. */

export const fetchCorridor = async (lat: number, lng: number, radiusM = 1000): Promise<CorridorPlace[]> => {
  const data = await apiFetch<{ places: CorridorPlace[] }>('POST', '/api/corridor', {
    lat,
    lng,
    radius_m: radiusM,
  });
  return data.places;
};

const BATCH = 10; // Worker fingerprints at most 10 sites per call

/**
 * Fingerprint all pending places in batches of 10; `onBatch` fires after each batch so the map
 * can recolor pins progressively. Batches run sequentially to stay polite with the Worker.
 */
export const fingerprintPending = async (
  places: readonly CorridorPlace[],
  onBatch: (results: Record<string, DetectedPos>) => void,
): Promise<Record<string, DetectedPos>> => {
  const pending = places.filter((p) => p.detected_pos === 'pending').map((p) => ({ place_id: p.place_id, website: p.website }));
  const all: Record<string, DetectedPos> = {};
  for (let i = 0; i < pending.length; i += BATCH) {
    const slice = pending.slice(i, i + BATCH);
    const data = await apiFetch<{ results: Record<string, DetectedPos> }>(
      'POST',
      '/api/corridor/fingerprint',
      {
        places: slice,
      },
    );
    Object.assign(all, data.results);
    onBatch(data.results);
  }
  return all;
};

// ---------------------------------------------------------------- route persistence (D27)

export type { RouteStop, StoredRoute } from '../../../shared/types';

export {
  applyServerRoutes,
  clearRoute,
  keepRouteSettings,
  loadRoute,
  loadRouteSettings,
  ROUTE_CHANGED,
  saveRoute,
  unwatched,
} from './dayRoutes';

export const toRouteStop = (p: CorridorPlace, note?: string): RouteStop & { lat: number; lng: number } => ({
  place_id: p.place_id,
  source: p.source ?? 'google',
  coordinates_at: p.coordinates_at,
  name: p.name,
  address: p.formatted_address,
  lat: p.lat,
  lng: p.lng,
  types: p.types,
  detected_pos: p.detected_pos,
  ...(p.lead_id ? { lead_id: p.lead_id } : {}),
  ...(note ? { note } : {}),
});

/** A saved stop back as a scout pick (Edit on a built route); its note stays on the stored route. */
export const placeFromStop = (s: RouteStop & { lat: number; lng: number }): CorridorPlace => ({
  place_id: s.place_id,
  coordinates_at: s.coordinates_at,
  source: s.source,
  name: s.name,
  formatted_address: s.address,
  lat: s.lat,
  lng: s.lng,
  types: s.types,
  website: null,
  business_status: null,
  detected_pos: s.detected_pos,
  ...(s.lead_id ? { lead_id: s.lead_id } : {}),
});

/** Google Maps turn-by-turn deep link for one stop. */
/** A stop's Google place id; Plan tomorrow's follow-ups and new businesses are known by `lead:` or
 *  `signal:` ids instead (D83), which Google must never be sent. */
export const googlePlaceId = (stop: Pick<RouteStop, 'place_id'>): string | null =>
  /^(lead|signal|roster):/.test(stop.place_id) ? null : stop.place_id;

export const navigateUrl = (stop: RouteStop): string => {
  if (stop.location_expired || stop.lat === null || stop.lng === null) return '';
  const dest = encodeURIComponent(stop.address ?? `${stop.lat},${stop.lng}`);
  const place = googlePlaceId(stop);
  const base = `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
  return place ? `${base}&destination_place_id=${encodeURIComponent(place)}` : base;
};
